'use client'

import { useTranslations } from 'next-intl'
import { Wrench, Snowflake, Bike } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { getLocalizedSlug } from '@/lib/localized-slugs'

interface ServicesCtaBlockProps {
  locale: string
  season: 'winter' | 'summer'
  variant?: 'default' | 'compact'
}

export default function ServicesCtaBlock({
  locale,
  season,
  variant = 'default'
}: ServicesCtaBlockProps) {
  const t = useTranslations('ServicesCTA')

  // Get localized URLs for the separate location and atelier pages
  const locationPageKey = season === 'winter' ? 'location-winter' : 'location-summer'
  const atelierPageKey = season === 'winter' ? 'atelier-winter' : 'atelier-summer'
  const locationSlug = getLocalizedSlug(locationPageKey, locale as 'en' | 'fr' | 'nl' | 'es' | 'it')
  const atelierSlug = getLocalizedSlug(atelierPageKey, locale as 'en' | 'fr' | 'nl' | 'es' | 'it')

  const rentalUrl = `/${locale}/${locationSlug}`
  const workshopUrl = `/${locale}/${atelierSlug}`

  const RentalIcon = season === 'winter' ? Snowflake : Bike
  const rentalImage = season === 'winter'
    ? '/7-laux-ski-snowboard-rental-location-snowpark-ressort.png'
    : '/7-laux-mtb-emtb-vtt-rental-location.png'
  const workshopImage = season === 'winter'
    ? '/7laux-snowboard-ambiance-detente-apres-ski.jpg'
    : '/vtt-enduro-dh-prapoutel-7laux-reparation-entretien-location.jpg'

  if (variant === 'compact') {
    return (
      <div className="my-12 p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl border border-primary/20">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="text-center md:text-left">
            <h3 className="text-xl font-bold mb-1">
              {t(`${season}.compact.heading`)}
            </h3>
            <p className="text-foreground text-sm font-bold">
              {t(`${season}.compact.subheading`)}
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              href={rentalUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all text-sm whitespace-nowrap"
            >
              <RentalIcon className="w-4 h-4" />
              {t(`${season}.rentalButton`)}
            </Link>
            <Link
              href={workshopUrl}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all text-sm whitespace-nowrap"
            >
              <Wrench className="w-4 h-4" />
              {t(`${season}.workshopButton`)}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section className="my-16 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Rental CTA Card */}
          <div className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-border hover:border-foreground/20 transition-all duration-300">
            <div className="relative z-10 flex flex-1 min-h-[10rem]">
              {/* Thumbnail */}
              <div className="relative w-2/5 flex-shrink-0 self-stretch">
                <Image
                  src={rentalImage}
                  alt=""
                  fill
                  sizes="(max-width: 768px) 40vw, 20vw"
                  className="object-cover object-top"
                />
              </div>

              <div className="flex-1 p-4 sm:p-6 flex flex-col">
                <h3 className="text-2xl sm:text-3xl leading-none font-bold font-[family-name:var(--font-bebas-neue)] text-foreground mb-3">
                  {t(`${season}.rental.heading`)}
                </h3>

                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                  {t(`${season}.rental.description`)}
                </p>

                <Link
                  href={rentalUrl}
                  className="mt-auto self-start inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                >
                  <RentalIcon className="w-4 h-4" />
                  {t(`${season}.rentalButton`)}
                </Link>
              </div>
            </div>
          </div>

          {/* Workshop CTA Card */}
          <div className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-border hover:border-foreground/20 transition-all duration-300">
            <div className="relative z-10 flex flex-1 min-h-[10rem]">
              {/* Thumbnail */}
              <div className="relative w-2/5 flex-shrink-0 self-stretch">
                <Image
                  src={workshopImage}
                  alt=""
                  fill
                  sizes="(max-width: 768px) 40vw, 20vw"
                  className="object-cover object-top"
                />
              </div>

              <div className="flex-1 p-4 sm:p-6 flex flex-col">
                <h3 className="text-2xl sm:text-3xl leading-none font-bold font-[family-name:var(--font-bebas-neue)] text-foreground mb-3">
                  {t(`${season}.workshop.heading`)}
                </h3>

                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                  {t(`${season}.workshop.description`)}
                </p>

                <Link
                  href={workshopUrl}
                  className="mt-auto self-start inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white rounded-lg font-bold hover:opacity-90 transition-all duration-300 hover:scale-105 text-sm"
                >
                  <Wrench className="w-4 h-4" />
                  {t(`${season}.workshopButton`)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
